import { useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { getProjectsSummaryStats, ProjectsSummaryStats } from '../Api/Projects/projectsApi';
import { accountScopedRead } from './accountScopedRead';

const subject = new BehaviorSubject<ProjectsSummaryStats | null>(null);
const loadingSubject = new BehaviorSubject<boolean>(false);

export const getProjectsSummaryStatsSubject = (): BehaviorSubject<ProjectsSummaryStats | null> => subject;
export const getProjectsSummaryStatsLoadingSubject = (): BehaviorSubject<boolean> => loadingSubject;

// A failed load (e.g. 404 while the backend flag is off) just hides the widget — no global banner.
const reads = accountScopedRead({ subject, request: getProjectsSummaryStats, empty: null, toValue: (data) => data });

export const refreshProjectsSummaryStats = reads.refresh;

export interface UseProjectsSummaryStatsReturn {
  stats: ProjectsSummaryStats | null;
  loading: boolean;
  fetchStats: (forceRefresh?: boolean) => Promise<void>;
  clearStats: () => void;
}

export const useProjectsSummaryStats = (): UseProjectsSummaryStatsReturn => {
  const [stats, setStats] = useState<ProjectsSummaryStats | null>(subject.getValue());
  const [loading, setLoading] = useState<boolean>(loadingSubject.getValue());

  useEffect(() => {
    const statsSub = subject.subscribe(setStats);
    const loadingSub = loadingSubject.subscribe(setLoading);
    return () => {
      statsSub.unsubscribe();
      loadingSub.unsubscribe();
    };
  }, []);

  const fetchStats = async (forceRefresh = false): Promise<void> => {
    if (loadingSubject.getValue()) return;
    if (subject.getValue() && !forceRefresh) return;

    loadingSubject.next(true);
    try {
      await reads.load();
    } finally {
      loadingSubject.next(false);
    }
  };

  const clearStats = () => subject.next(null);

  return { stats, loading, fetchStats, clearStats };
};
